/**
 * API funkce pro Magic Link autentizaci
 * Tyto funkce doplňují vygenerované NSwag typy
 */

import { getApiBaseUrl, getCommonHeaders } from '../config';
import { clearSession, setSession } from './sessionStore';

export interface MagicLinkRequest {
  email: string;
}

export interface ActivateTokenRequest {
  token: string;
}

export interface ActivationResultModel {
  tokenType: 'NewKeyActivation' | 'EditAccess';
  apiKey?: {
    apiKey: string;
    customerId: number;
    email: string;
  };
  /** Session token — uložíme do sessionStorage a posíláme v X-Session hlavičce */
  sessionId?: string;
  /** ISO UTC string */
  expiresAt?: string;
}

export interface WidgetApiKeyModel {
  apiKey: string;
  customerId: number;
  email: string;
  shopUrls?: string[];
  created: string;
  isActive: boolean;
}

export interface WidgetApiKeyClaimModel {
  claimType: string;
  claimValue: string;
}

export interface UpdateWidgetApiKeyClaimsRequest {
  claims: WidgetApiKeyClaimModel[];
}

/**
 * Odeslání žádosti o magic link pro editaci
 */
export async function requestMagicLink(
  email: string,
  recaptchaToken: string
): Promise<void> {
  const response = await fetch(`${getApiBaseUrl()}/v1/magic-link-request`, {
    method: 'POST',
    headers: {
      ...getCommonHeaders(),
      'Content-Type': 'application/json',
      'x-recaptcha-token': recaptchaToken,
    },
    body: JSON.stringify({ email } as MagicLinkRequest),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to send magic link');
  }
}

/**
 * Aktivace tokenu — BE vrátí `{ sessionId, expiresAt }` (plus tokenType/apiKey).
 * sessionId uložíme do sessionStorage a `getCommonHeaders` ho pak posílá
 * v hlavičce `X-Session` v každém dalším volání.
 */
export async function activateToken(
  token: string
): Promise<ActivationResultModel> {
  const response = await fetch(`${getApiBaseUrl()}/v1/activate`, {
    method: 'POST',
    headers: {
      ...getCommonHeaders(),
      'Content-Type': 'application/json',
    },
    credentials: 'include', // fallback pro HttpOnly cookie session
    body: JSON.stringify({ token } as ActivateTokenRequest),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    if (response.status === 409) {
      throw new Error('TOKEN_ALREADY_USED');
    } else if (response.status === 400) {
      throw new Error('TOKEN_EXPIRED');
    }
    throw new Error(error.detail || 'TOKEN_NOT_FOUND');
  }

  const result: ActivationResultModel = await response.json();
  if (result.sessionId && result.expiresAt) {
    setSession(result.sessionId, new Date(result.expiresAt).getTime());
  }
  return result;
}

/**
 * Načtení API klíčů - používá session cookie
 */
export async function getApiKeysBySession(): Promise<WidgetApiKeyModel[]> {
  const response = await fetch(`${getApiBaseUrl()}/v1/api-keys`, {
    method: 'GET',
    headers: {
      ...getCommonHeaders(),
      'Accept': 'application/json',
    },
    credentials: 'include', // Důležité pro odeslání cookies
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('SESSION_EXPIRED');
    }
    throw new Error('Failed to fetch API keys');
  }

  return response.json();
}

/**
 * Načtení claims pro API klíč - používá session cookie
 */
export async function getApiKeyClaimsBySession(
  widgetKey: string
): Promise<WidgetApiKeyClaimModel[]> {
  const response = await fetch(`${getApiBaseUrl()}/v1/api-key-claims`, {
    method: 'GET',
    headers: {
      ...getCommonHeaders(),
      'Accept': 'application/json',
      'X-Widget-Key': widgetKey,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('SESSION_EXPIRED');
    }
    throw new Error('Failed to fetch API key claims');
  }

  return response.json();
}

/**
 * Aktualizace claims pro API klíč - používá session cookie
 */
export async function updateApiKeyClaimsBySession(
  widgetKey: string,
  claims: WidgetApiKeyClaimModel[]
): Promise<void> {
  const response = await fetch(`${getApiBaseUrl()}/v1/api-key-claims`, {
    method: 'PUT',
    headers: {
      ...getCommonHeaders(),
      'Content-Type': 'application/json',
      'X-Widget-Key': widgetKey,
    },
    credentials: 'include',
    body: JSON.stringify({ claims } as UpdateWidgetApiKeyClaimsRequest),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('SESSION_EXPIRED');
    }
    throw new Error('Failed to update API key claims');
  }
}

/**
 * Odhlášení — best-effort volání BE, session na FE zahodíme tak jako tak.
 */
export async function logout(): Promise<void> {
  try {
    await fetch(`${getApiBaseUrl()}/v1/logout`, {
      method: 'POST',
      headers: {
        ...getCommonHeaders(),
      },
      credentials: 'include',
    });
  } catch {
    // ignore — session na FE smažeme tak jako tak
  }
  clearSession();
}

export interface WidgetCountryModel {
  countryCode: string;
  names: { language: string; name: string }[];
}

/**
 * Načtení dostupných zemí pro widget - používá session cookie
 */
export async function getWidgetCountries(): Promise<WidgetCountryModel[]> {
  const response = await fetch(`${getApiBaseUrl()}/v1/widget-countries`, {
    method: 'GET',
    headers: {
      ...getCommonHeaders(),
      'Accept': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('SESSION_EXPIRED');
    }
    throw new Error('Failed to fetch widget countries');
  }

  return response.json();
}

export interface AccessPointTypeModel {
  code: string;
  name: string;
  smartToBox: boolean;
}

/**
 * Načtení typů výdejních míst - používá session cookie
 */
export async function getAccessPointTypes(): Promise<AccessPointTypeModel[]> {
  const response = await fetch(`${getApiBaseUrl()}/v1/accesspoint-types`, {
    method: 'GET',
    headers: {
      ...getCommonHeaders(),
      'Accept': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('SESSION_EXPIRED');
    }
    throw new Error('Failed to fetch access point types');
  }

  return response.json();
}
